import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementBlock as _createElementBlock, Suspense as _Suspense, isRef as _isRef, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 2 }
const _hoisted_2 = { class: "flex flex-column w-20rem" }
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = { key: 3 }
const _hoisted_5 = { class: "flex flex-column w-20rem font-bold" }
const _hoisted_6 = { class: "mb-2" }
const _hoisted_7 = { class: "flex align-items-center" }

import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useAuthStore, useCalendarStore, useEvaluationStore, useEventsModalsStore, useTherapyStore } from '@/store';
import {
  AttendeeDropdownOptionInterface,
  Attendee,
  EventDeleteRequestInterface,
  DetailedEventInterface,
} from '@/types';
import { usePersonTypes } from '@/composables';
import Dialog from 'primevue/dialog';
import RadioButton from 'primevue/radiobutton';
import DisciplineIcon from '@/components/DisciplineIcon/DisciplineIcon.vue';
import EventModal from '@/views/Calendar/components/EventModal/EventModal.vue';
import DocumentEventModal from '@/views/Calendar/components/DocumentEventModal.vue';
import PastDocumentations from '@/views/Calendar/components/PastDocumentations.vue';
import { storeToRefs } from 'pinia';
import { EventClickArg } from '@fullcalendar/core';
import InfoTooltip from '../InfoTooltip/InfoTooltip.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'EventsModals',
  setup(__props) {

const evaluationStore = useEvaluationStore();
const calendarStore = useCalendarStore();
const eventsModalsStore = useEventsModalsStore();
const therapyStore = useTherapyStore();

const { eventToEditSelected, displayEventModal, eventToDocumentOrEdit, displayDocumentEventModal } =
  storeToRefs(eventsModalsStore);

const toast = useToast();
const selectedAttendees = ref<AttendeeDropdownOptionInterface[]>([]);
const displayPastDocumentations = ref(false);
const pastDocumentationsStudent = ref();
const confirmDeleteOpen = ref(false);
const deleteEventConditions = ref<'this' | 'future'>('this');
const isCaseManagerViewOnly = computed(() => {
  if (!usePersonTypes.isCaseManager()) return false;
  return eventToDocumentOrEdit.value?.host_id !== useAuthStore().user?.id;
});
const isSupportAdminOrSchoolAdmin = ref(usePersonTypes.isSupportAdminOrSchoolAdmin());
const multiAttendee = ref(0);

const eventTypeTitle = computed(() => {
  const needPrefix = ['Session', 'Response to Intervention'].includes(
    eventToDocumentOrEdit.value?.therapy_event_type as string,
  );
  const prefix = needPrefix && multiAttendee.value > 1 ? 'Group' : 'Individual';

  return needPrefix
    ? `${prefix} ${eventToDocumentOrEdit.value?.therapy_event_type}`
    : eventToDocumentOrEdit.value?.therapy_event_type;
});

const modalTitle = computed(() => (eventToDocumentOrEdit.value ? `Edit ${eventTypeTitle.value}` : 'New Event'));

const togglePastDocuments = async (attendee: Attendee) => {
  if (calendarStore.pastDocumentationsPaginateState[attendee.id] === undefined) {
    await calendarStore.getPastDocumentations(eventToDocumentOrEdit.value?.document_date as string, attendee.id);
  }

  if (!displayPastDocumentations.value) {
    pastDocumentationsStudent.value = attendee;
    displayPastDocumentations.value = true;
    return;
  }

  if (pastDocumentationsStudent.value.id === attendee.id) {
    displayPastDocumentations.value = false;
    return;
  }

  pastDocumentationsStudent.value = attendee;
};

const loadStudentPastSessions = async () => {
  await calendarStore.getPastDocumentations(
    eventToDocumentOrEdit.value?.document_date as string,
    pastDocumentationsStudent.value.id,
  );
};

const closePastDocuments = () => {
  displayPastDocumentations.value = false;
};

const toggleDocumentEventModal = () => {
  displayDocumentEventModal.value = !displayDocumentEventModal.value;
  if (!displayEventModal.value) displayPastDocumentations.value = false;
};

const editEvent = () => {
  toggleDocumentEventModal();
  eventsModalsStore.onEditEvent(eventsModalsStore.clickedEventClickArgs as EventClickArg['event']);
};

const onSaveDocumentEvent = async (responseStatus: number, responseText: string) => {
  await calendarStore.getEvents({ start_at: calendarStore.startDate, end_at: calendarStore.endDate });

  if (responseStatus === 200) {
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Your event has been documented.',
      life: 3000,
    });
    toggleDocumentEventModal();
    return;
  }

  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: responseText,
    life: 3000,
  });
};

const documentFutureEvent = () => {
  eventsModalsStore.toggleEventModal();
  eventsModalsStore.onDocumentEvent(eventsModalsStore.clickedEventClickArgs as EventClickArg['event']);
};

const saveNewEventFailed = (errorResponse: { data: { message?: string; errors: Record<string, string[]> } }) => {
  const errorArr = ['Your event was unable to be created:', errorResponse.data.message];
  const detail: string = errorArr.join('\n');

  displayEventModal.value = false;
  toast.add({
    severity: 'error',
    summary: 'Event Creation Failed',
    detail,
    life: 3000,
  });
};

const deleteEventSuccess = async () => {
  displayEventModal.value = false;
  toast.add({
    severity: 'success',
    summary: 'Event Deleted',
    detail: 'Your event has been deleted successfully!',
    life: 3000,
  });

  await calendarStore.getEvents({ start_at: calendarStore.startDate, end_at: calendarStore.endDate });
};

const deleteEvent = async () => {
  const payload: EventDeleteRequestInterface = {
    id: eventToDocumentOrEdit.value?.id as number,
    date: eventToDocumentOrEdit.value?.start_at as string,
    mode: deleteEventConditions.value,
  };
  await therapyStore.deleteEvent(payload).catch(deleteEventFailed);
  await deleteEventSuccess();
};

const deleteEventFailed = (errorResponse: { message?: string; errors: Record<string, string[]> }) => {
  const errorArr = ['Your event failed to delete.', errorResponse.message];
  const detail: string = errorArr.join('\n');

  displayEventModal.value = false;
  toast.add({
    severity: 'error',
    summary: 'Event Delete Failed',
    detail,
    life: 3000,
  });
};

const saveNewEvent = async () => {
  displayEventModal.value = false;
  selectedAttendees.value = [];
  toast.add({
    severity: 'success',
    summary: 'Event Created',
    detail: 'Your event has been successfully created!',
    life: 3000,
  });

  await calendarStore.getEvents({ start_at: calendarStore.startDate, end_at: calendarStore.endDate });
  await evaluationStore.getEvaluations();
};

const changeMultiAttendee = (value: Attendee[]) => (multiAttendee.value = value.length);

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_ConfirmAction = _resolveComponent("ConfirmAction")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(Dialog), {
      visible: _unref(displayEventModal),
      "onUpdate:visible": _cache[7] || (_cache[7] = ($event: any) => (_isRef(displayEventModal) ? (displayEventModal).value = $event : null)),
      modal: true,
      draggable: false,
      class: "new-event-modal"
    }, {
      header: _withCtx(() => [
        _createElementVNode("h2", null, _toDisplayString(modalTitle.value), 1),
        (_unref(eventToDocumentOrEdit) && _unref(eventToDocumentOrEdit).host_timezone_name)
          ? (_openBlock(), _createBlock(InfoTooltip, { key: 0 }, {
              default: _withCtx(() => [
                _createElementVNode("span", null, "Event ID: " + _toDisplayString(_unref(eventToDocumentOrEdit).id), 1),
                _createElementVNode("span", null, " Created: " + _toDisplayString(_unref(eventToDocumentOrEdit).created_at) + " " + _toDisplayString(_unref(eventToDocumentOrEdit).host_timezone_abbr), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(eventToEditSelected) && !isCaseManagerViewOnly.value)
          ? (_openBlock(), _createBlock(_component_Icon, {
              key: 1,
              name: "delete",
              class: "material-icons p-dialog-header-icon trash-can-icon ml-auto cursor-pointer",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (confirmDeleteOpen.value = true))
            }))
          : _createCommentVNode("", true),
        (_unref(calendarStore).isCaseloadManagement(eventTypeTitle.value) || _unref(eventsModalsStore).isSingleEvent)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              (confirmDeleteOpen.value)
                ? (_openBlock(), _createBlock(_component_ConfirmAction, {
                    key: 0,
                    header: "Delete Event",
                    "accept-label": "Delete Event",
                    onAccept: deleteEvent,
                    onCancel: _cache[1] || (_cache[1] = ($event: any) => (confirmDeleteOpen.value = false))
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                          _createVNode(_component_Icon, {
                            name: "warning",
                            class: "text-color-yellow-dark"
                          }),
                          _cache[9] || (_cache[9] = _createElementVNode("span", {
                            class: "inline-block mt-1 ml-2 text-base",
                            textContent: 'This action cannot be undone'
                          }, null, -1))
                        ])
                      ])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (confirmDeleteOpen.value)
                ? (_openBlock(), _createBlock(_component_ConfirmAction, {
                    key: 0,
                    header: "Delete Recurring Event",
                    "accept-label": "Delete",
                    onAccept: deleteEvent,
                    onCancel: _cache[4] || (_cache[4] = ($event: any) => (confirmDeleteOpen.value = false))
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("div", _hoisted_6, [
                          _createVNode(_unref(RadioButton), {
                            modelValue: deleteEventConditions.value,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((deleteEventConditions).value = $event)),
                            name: "deleteThis",
                            value: "this"
                          }, null, 8, ["modelValue"]),
                          _cache[10] || (_cache[10] = _createElementVNode("label", {
                            for: "deleteThis",
                            class: "ml-2"
                          }, "This event", -1))
                        ]),
                        _createElementVNode("div", null, [
                          _createVNode(_unref(RadioButton), {
                            modelValue: deleteEventConditions.value,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((deleteEventConditions).value = $event)),
                            name: "deleteAll",
                            value: "future"
                          }, null, 8, ["modelValue"]),
                          _cache[11] || (_cache[11] = _createElementVNode("label", {
                            for: "deleteAll",
                            class: "ml-2"
                          }, "This and following events", -1))
                        ])
                      ])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]))
      ]),
      default: _withCtx(() => [
        (_openBlock(), _createBlock(_Suspense, null, {
          default: _withCtx(() => [
            (_unref(eventToDocumentOrEdit))
              ? (_openBlock(), _createBlock(EventModal, {
                  key: 0,
                  "event-to-edit": (_unref(eventToDocumentOrEdit) as DetailedEventInterface),
                  "event-to-edit-selected": _unref(eventToEditSelected),
                  onSaveNewEvent: saveNewEvent,
                  onSaveEventFailed: saveNewEventFailed,
                  onCloseModal: _cache[5] || (_cache[5] = ($event: any) => (_unref(eventsModalsStore).toggleEventModal())),
                  onDocumentEvent: documentFutureEvent
                }, null, 8, ["event-to-edit", "event-to-edit-selected"]))
              : (_openBlock(), _createBlock(EventModal, {
                  key: 1,
                  "event-to-edit": _unref(eventToDocumentOrEdit),
                  "event-to-edit-selected": _unref(eventToEditSelected),
                  onSaveNewEvent: saveNewEvent,
                  onSaveEventFailed: saveNewEventFailed,
                  onCloseModal: _cache[6] || (_cache[6] = ($event: any) => (_unref(eventsModalsStore).toggleEventModal()))
                }, null, 8, ["event-to-edit", "event-to-edit-selected"]))
          ]),
          _: 1
        }))
      ]),
      _: 1
    }, 8, ["visible"]),
    _createElementVNode("div", {
      class: _normalizeClass({ 'document-event-modal-with-drawer': displayPastDocumentations.value })
    }, [
      _createVNode(_unref(Dialog), {
        visible: _unref(displayDocumentEventModal),
        "onUpdate:visible": _cache[8] || (_cache[8] = ($event: any) => (_isRef(displayDocumentEventModal) ? (displayDocumentEventModal).value = $event : null)),
        "append-to": "self",
        modal: true,
        draggable: false,
        class: "document-event-modal"
      }, {
        header: _withCtx(() => [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("h2", null, _toDisplayString(!isSupportAdminOrSchoolAdmin.value && !isCaseManagerViewOnly.value ? 'Document ' + eventTypeTitle.value : eventTypeTitle.value), 1),
            (_unref(eventToDocumentOrEdit) && _unref(eventToDocumentOrEdit).discipline)
              ? (_openBlock(), _createBlock(DisciplineIcon, {
                  key: 0,
                  class: "mx-2",
                  code: _unref(eventToDocumentOrEdit).discipline.code
                }, null, 8, ["code"]))
              : _createCommentVNode("", true),
            (_unref(eventToDocumentOrEdit) && _unref(eventToDocumentOrEdit).host_timezone_name)
              ? (_openBlock(), _createBlock(InfoTooltip, { key: 1 }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", null, "Event ID: " + _toDisplayString(_unref(eventToDocumentOrEdit).id), 1),
                    _createElementVNode("span", null, " Created:" + _toDisplayString(_unref(eventToDocumentOrEdit).created_at) + " " + _toDisplayString(_unref(eventToDocumentOrEdit).host_timezone_abbr), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(DocumentEventModal, {
            "document-modal-opened": _unref(displayDocumentEventModal) && displayPastDocumentations.value,
            "event-to-document": _unref(eventToDocumentOrEdit),
            onOpenPastDocuments: togglePastDocuments,
            onCloseModal: toggleDocumentEventModal,
            onSaveDocumentEvent: onSaveDocumentEvent,
            onEditEvent: editEvent,
            onChangeMultiAttendee: changeMultiAttendee
          }, null, 8, ["document-modal-opened", "event-to-document"])
        ]),
        _: 1
      }, 8, ["visible"])
    ], 2),
    _createVNode(PastDocumentations, {
      student: pastDocumentationsStudent.value,
      open: _unref(displayDocumentEventModal) && displayPastDocumentations.value,
      onLoadStudentPastSessions: loadStudentPastSessions,
      onClosePastDocumentations: closePastDocuments
    }, null, 8, ["student", "open"])
  ]))
}
}

})